import React, { useState, useEffect } from 'react';
import { X, Printer, Download, QrCode, Package } from 'lucide-react';
import inventoryService from '../services/inventoryService';
import toast from 'react-hot-toast';

const LabelPrintView = ({ stockEntry, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [labels, setLabels] = useState([]);
  const [printData, setPrintData] = useState(null);

  useEffect(() => {
    if (stockEntry?.variante_id) {
      loadLabels();
    } else if (stockEntry?.id) {
      loadLabels();
    }
  }, [stockEntry?.variante_id, stockEntry?.id]);

  const loadLabels = async () => {
    setLoading(true);
    try {
      let response;
      if (stockEntry?.variante_id) {
        response = await inventoryService.getLabelsByVariant(stockEntry.variante_id);
      } else {
        response = await inventoryService.getStockLabels(stockEntry.id);
      }
      if (response.success) {
        setLabels(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar etiquetas:', error);
    } finally {
      setLoading(false);
    }
  };

  const handlePrintLabel = async (label) => {
    try {
      const response = await inventoryService.printSmallLabel(label.qr_code);
      if (response.success) {
        setPrintData(response.data);
        setTimeout(() => {
          const printWindow = window.open('', '_blank', 'width=300,height=300');
          printWindow.document.write(response.data.html);
          printWindow.document.close();
          printWindow.focus();
          printWindow.print();
          printWindow.close();
        }, 250);
      }
    } catch (error) {
      toast.error('Erro ao imprimir etiqueta');
    }
  };

  const handlePrintAll = async () => {
    if (labels.length === 0) {
      toast.error('Nenhuma etiqueta para imprimir');
      return;
    }

    try {
      let response;
      if (stockEntry?.variante_id) {
        response = await inventoryService.printLabelsByVariant(stockEntry.variante_id, labels.length);
      } else {
        response = await inventoryService.printBatchLabels(stockEntry.id, labels.length);
      }
      if (response.success) {
        const printWindow = window.open('', '_blank', 'width=400,height=600');
        printWindow.document.write(response.data.html);
        printWindow.document.close();
        printWindow.focus();
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 250);
      }
    } catch (error) {
      toast.error('Erro ao imprimir etiquetas');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <div className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-bold">Etiquetas de Estoque</h2>
          </div>
          <button onClick={onClose} className="text-secondary-400 hover:text-secondary-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {/* Product Info */}
          <div className="bg-secondary-50 rounded-lg p-3 mb-4">
            <p className="font-medium">{stockEntry?.produto_nome}</p>
            <p className="text-sm text-secondary-600">
              {stockEntry?.cor && `Cor: ${stockEntry.cor}`}
              {stockEntry?.tamanho_label && ` | Tamanho: ${stockEntry.tamanho_label}`}
            </p>
            <p className="text-sm">
              <span className="font-medium">{labels.length}</span> etiquetas geradas
            </p>
          </div>

          {loading ? (
            <div className="text-center py-8 text-secondary-500">Carregando etiquetas...</div>
          ) : labels.length === 0 ? (
            <div className="text-center py-8 text-secondary-500">
              Nenhuma etiqueta encontrada para este estoque.
            </div>
          ) : (
            <>
              {/* Labels Grid */}
              <div className="grid grid-cols-5 gap-2 mb-4">
                {labels.map((label) => (
                  <button
                    key={label.id}
                    onClick={() => handlePrintLabel(label)}
                    className="border border-secondary-300 rounded-lg p-2 hover:bg-secondary-50 hover:border-primary-500 transition-colors"
                  >
                    <QrCode className="w-6 h-6 mx-auto mb-1 text-primary-600" />
                    <span className="text-xs font-mono block truncate" title={label.numero_serie}>
                      {label.numero_serie?.split('-').pop()}
                    </span>
                  </button>
                ))}
              </div>

              {/* Label Preview */}
              {printData && (
                <div className="border-2 border-primary-500 rounded-lg p-4">
                  <h3 className="text-sm font-medium mb-2">Preview (5x5cm)</h3>
                  <div 
                    dangerouslySetInnerHTML={{ __html: printData.html.replace(/<style>[\s\S]*<\/style>/, '').replace(/<\/?html>[\s\S]*<\/html>/, '').replace(/<\/?head>[\s\S]*<\/head>/, '').replace(/<\/?body>/, '').replace(/<\/body>/, '') }}
                    className="bg-white border border-black p-1 mx-auto"
                    style={{ width: '189px', height: '189px' }}
                  />
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-center gap-3 p-4 border-t bg-secondary-50">
          <button
            onClick={loadLabels}
            className="flex items-center gap-2 px-4 py-2 bg-secondary-100 text-secondary-700 rounded-md hover:bg-secondary-200"
          >
            Atualizar
          </button>
          <button
            onClick={handlePrintAll}
            disabled={labels.length === 0}
            className="flex items-center gap-2 px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            <Printer className="w-4 h-4" />
            Imprimir Todas ({labels.length})
          </button>
        </div>
      </div>
    </div>
  );
};

export default LabelPrintView;